import { z } from 'zod';
import { mimeTypeConfigsByType } from '../types/mimeTypes';

export const contentFieldSchema = z.object({
  // We nest type, file and url inside content so that we can use a superrefine to validate it.

  /* 
  # Since we want this form to act as a step by step wizard, we need to validate each field at each step.
  # Without nesting, and instead adding a superrefine to the contentFieldSchema as a whole, we fall into the trap of
  # validation for specific steps passing, even though the they clearly should fail at the superrefine stage.
  # This happens because zod validation triggers on each field individually, and the superrefine is only run once all fields have passed.
  # if a later step fails, the superrefine will not run, and the form will pass validation for the earlier steps.
  */

  // in this case the superrefine is used to validate that the content type selected matches either the file or url provided,
  // and that only one of the file or url fields is completed, not both (to avoid confusion)
  content: z
    .object({
      type: z.string({
        required_error: 'Please select a content type',
      }),
      file: z.instanceof(File).nullable().optional(),
      url: z.string().url('Invalid URL format').nullable().optional(),
    })
    .superRefine((data, ctx) => {
      const { type, file, url } = data;

      const allowedTypes = mimeTypeConfigsByType[type]?.map(
        (type) => type.mimeType
      );

      const allowedExtenstions = mimeTypeConfigsByType[type]?.map(
        (type) => type.extension
      );

      if (!allowedTypes) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid content type',
        });
        return;
      }

      if (type === 'website') {
        if (!url || file) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Website content should only have a URL',
          });
        }
        return;
      }

      if (file && !allowedTypes.includes(file.type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Content must be one of these types: ${allowedExtenstions.join(
            ', '
          )}`,
        });
        return;
      }

      if (url) {
        const hasValidExtension = allowedExtenstions.some((ext: string) =>
          url.toLowerCase().endsWith(`.${ext}`)
        );
        if (!hasValidExtension) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Content must be one of these types: ${allowedExtenstions.join(
              ', '
            )}`,
          });
          return;
        }
      }

      if ((!file && !url) || (!!file && !!url)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either a file OR a URL must be provided',
        });
      }
    }),
});

export const thumbnailFieldSchema = z.object({
  thumbnail: z.string().min(1, 'A thumbnail is required'),
});

export const contentDetailsFieldSchema = z.object({
  title: z.string().min(1, 'A title is required'),
  description: z.string().optional(),
  printable: z.boolean().optional(),
  comments: z.boolean().optional(),
  deleted: z.boolean().optional(),
});

export const cloudsFieldSchema = z.object({
  clouds: z.array(z.string()),
});

export const termsFieldSchema = z.object({
  termsAndConditions: z
    .boolean()
    .optional()
    .refine((data) => data === true, {
      message: 'You must agree to the terms and conditions',
    }),
});

export type ContentFieldData = z.infer<typeof contentFieldSchema>;
export type ThumbnailFieldData = z.infer<typeof thumbnailFieldSchema>;
export type ContentDetailsFieldData = z.infer<typeof contentDetailsFieldSchema>;
export type CloudsFieldData = z.infer<typeof cloudsFieldSchema>;
export type TermsFieldData = z.infer<typeof termsFieldSchema>;
